import React, { useRef, useState, useCallback } from 'react';
import { api } from '../../shared/utils/api';
import { aggregateStudyUnit, applyRateResponse } from '../../shared/utils/studyUnitAggregation';
import { bandHex } from './bandColors';

// Compact study-unit label row used in detailed-subtopics mode.
// Shows the study unit name + a small inline slider + aggregated % + count.
// NOT expandable — the L3 cards underneath provide the interaction surface.
// The slider rates the whole study unit (same preserve-is_estimated logic
// as StudyUnitRow). Optimistic update with revert + error toast on failure.
export default function StudyUnitGroupHeader({ studyUnit, members, onTopicsBulkUpdate }) {
  const [showError, setShowError] = useState(false);
  const errorTimer = useRef(null);
  const dragSnapshot = useRef(null);

  const agg = aggregateStudyUnit(members);
  const sliderValue = agg.confidence !== null ? Math.round(agg.confidence * 100) : 50;
  const pctText = agg.confidence !== null ? `${Math.round(agg.confidence * 100)}%` : '—';
  const pctColor = bandHex(agg.confidence);
  const subWord = agg.totalCount === 1 ? 'subtopic' : 'subtopics';

  const showToast = useCallback(() => {
    setShowError(true);
    clearTimeout(errorTimer.current);
    errorTimer.current = setTimeout(() => setShowError(false), 3000);
  }, []);

  const handleSliderChange = (e) => {
    const newConf = parseInt(e.target.value) / 100;
    if (!dragSnapshot.current) {
      dragSnapshot.current = members.map(m => ({ ...m }));
    }
    const optimistic = members.map(m => m.is_estimated
      ? { ...m, confidence: newConf, raw_confidence: newConf }
      : m
    );
    onTopicsBulkUpdate(studyUnit.member_topic_ids, optimistic);
  };

  const handleSliderRelease = async (e) => {
    const newConf = parseInt(e.target.value) / 100;
    const snapshot = dragSnapshot.current;
    dragSnapshot.current = null;
    try {
      const response = await api(`/api/study-units/${studyUnit.id}/rate`, {
        method: 'POST',
        body: JSON.stringify({ confidence: newConf }),
      });
      onTopicsBulkUpdate(studyUnit.member_topic_ids, applyRateResponse(members, response));
    } catch (err) {
      if (snapshot) onTopicsBulkUpdate(studyUnit.member_topic_ids, snapshot);
      showToast();
    }
  };

  return (
    <>
      <div className="su-group-header">
        <span className="su-group-name">{studyUnit.name}</span>
        <div className="su-group-meta">
          <span className="su-group-count">{agg.totalCount} {subWord}</span>
          {agg.confidence !== null ? (
            <>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={sliderValue}
                className="conf-slider su-group-slider"
                style={{
                  '--slider-color': pctColor,
                  background: `linear-gradient(to right, ${pctColor} ${sliderValue}%, var(--border) ${sliderValue}%)`,
                }}
                onChange={handleSliderChange}
                onMouseUp={handleSliderRelease}
                onTouchEnd={handleSliderRelease}
              />
              <span className="su-group-val" style={{ color: pctColor }}>{pctText}</span>
            </>
          ) : (
            <>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={50}
                className="conf-slider su-group-slider"
                style={{ background: `linear-gradient(to right, var(--border) 50%, var(--border) 50%)` }}
                onChange={handleSliderChange}
                onMouseUp={handleSliderRelease}
                onTouchEnd={handleSliderRelease}
              />
              <span className="su-group-val su-group-val-empty">—</span>
            </>
          )}
        </div>
      </div>
      {showError && (
        <div className="study-unit-toast">Couldn't save. Try again.</div>
      )}
    </>
  );
}
